import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  GraduationCap,
  Building2,
  BookOpen,
  FileText,
  BarChart3,
  Settings,
  HelpCircle,
  LogOut,
  ChevronRight,
  ChevronDown,
  Bell,
  Search,
  MapPin,
  Calendar,
  TrendingUp,
  Award,
  Clock,
  Shield,
  Database,
  Zap,
  CreditCard,
  DollarSign
} from 'lucide-react';

const NavigationItem = ({ to, icon: Icon, title, badge, isActive, onClick, children }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasChildren = children && children.length > 0;

  const handleClick = () => {
    if (hasChildren) {
      setIsExpanded(!isExpanded);
    }
    if (onClick) onClick();
  };

  return (
    <div className="w-full">
      <Link
        to={hasChildren ? '#' : to}
        onClick={handleClick}
        className={`
          flex items-center justify-between w-full px-4 py-3 rounded-lg transition-all duration-200 group
          ${isActive
            ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg'
            : 'text-gray-700 hover:bg-gray-100 hover:text-blue-600'
          }
        `}
      >
        <div className="flex items-center space-x-3">
          <Icon className={`h-5 w-5 ${isActive ? 'text-white' : 'text-gray-500 group-hover:text-blue-600'}`} />
          <span className="font-medium">{title}</span>
        </div>
        <div className="flex items-center space-x-2">
          {badge && (
            <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
              {badge}
            </span>
          )}
          {hasChildren && (
            <ChevronRight
              className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''} ${isActive ? 'text-white' : 'text-gray-400'}`}
            />
          )}
        </div>
      </Link>

      {hasChildren && isExpanded && (
        <div className="ml-4 mt-2 space-y-1 border-l-2 border-gray-200 pl-4">
          {children.map((child, index) => (
            <Link
              key={index}
              to={child.to}
              className="flex items-center space-x-3 px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-blue-600 transition-all duration-200"
            >
              <child.icon className="h-4 w-4" />
              <span className="text-sm">{child.title}</span>
              {child.badge && (
                <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                  {child.badge}
                </span>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export const MainNavigation = ({ institutionName, user, learningMode }) => {
  const location = useLocation();

  const navigationItems = [
    {
      to: '/',
      icon: LayoutDashboard,
      title: 'Dashboard',
      isActive: location.pathname === '/',
    },
    {
      to: '/students',
      icon: GraduationCap,
      title: 'Student Management',
      isActive: location.pathname === '/students',
    },
    {
      to: '/lecturers',
      icon: Users,
      title: learningMode === 'self_paced' ? 'Tutor Management' : 'Lecturer Management',
      isActive: location.pathname === '/lecturers',
    },
    {
      to: '/campuses',
      icon: Building2,
      title: 'Campus Management',
      isActive: location.pathname === '/campuses',
    },
    {
      icon: BookOpen,
      title: 'Academic Management',
      isActive: ['/courses', '/units', '/unit-costs'].includes(location.pathname),
      children: [
        {
          to: '/courses',
          icon: BookOpen,
          title: 'Courses',
        },
        {
          to: '/units',
          icon: FileText,
          title: 'Units',
        },
        {
          to: '/unit-costs',
          icon: Award,
          title: 'Unit Pricing',
        },
        {
          to: '/course-tracking',
          icon: TrendingUp,
          title: 'Course Tracking',
        }
      ]
    }
  ];

  const superAdminItems = [
    {
      to: '/super-admin',
      icon: LayoutDashboard,
      title: 'Platform Metrics',
      isActive: location.pathname === '/super-admin',
    },
    {
      to: '/super-admin/institutions',
      icon: Building2,
      title: 'Institution Management',
      isActive: location.pathname.startsWith('/super-admin/institutions'),
    },
    {
      to: '/super-admin/billing',
      icon: CreditCard,
      title: 'Global Billing Ledger',
      isActive: location.pathname === '/super-admin/billing',
    }
  ];

  const reportsItems = [
    {
      to: '/assigned-lecturers-report',
      icon: FileText,
      title: learningMode === 'self_paced' ? 'Tutor Assignments' : 'Lecturer Assignments',
      isActive: location.pathname === '/assigned-lecturers-report',
    },
    // Only show Attendance Reports for instructor-led
    ...(learningMode !== 'self_paced' ? [{
      to: '/attendance',
      icon: Calendar,
      title: 'Attendance Reports',
      isActive: location.pathname === '/attendance',
    }] : []),
    {
      to: '/payments',
      icon: Award,
      title: 'Payment Reports',
      isActive: location.pathname === '/payments',
    },
    {
      to: '/earnings',
      icon: DollarSign,
      title: 'Lecturer Earnings',
      isActive: location.pathname === '/earnings',
    },
    {
      to: '/analytics',
      icon: BarChart3,
      title: 'Analytics Dashboard',
      isActive: location.pathname === '/analytics',
    }
  ];

  const adminItems = [
    {
      to: '/subscription',
      icon: CreditCard,
      title: 'Subscription',
      isActive: location.pathname === '/subscription',
    },
    {
      to: '/mpesa-credentials',
      icon: CreditCard,
      title: 'M-Pesa Credentials',
      isActive: location.pathname === '/mpesa-credentials',
    },
    {
      to: '/settings',
      icon: Settings,
      title: 'System Settings',
      isActive: location.pathname === '/settings',
    },
    {
      to: '/users',
      icon: Shield,
      title: 'User Management',
      isActive: location.pathname === '/users',
    },
    {
      to: '/backup',
      icon: Database,
      title: 'Data Management',
      isActive: location.pathname === '/backup',
    }
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Logo Section */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
            <GraduationCap className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{institutionName || 'Institution Admin'}</h2>
            <p className="text-sm text-gray-500">Educational Portal</p>
          </div>
        </div>
      </div>


      {/* Navigation Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-6">
          {user?.role === 'super_admin' ? (
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Platform Management
              </h3>
              <div className="space-y-1">
                {superAdminItems.map((item, index) => (
                  <NavigationItem key={index} {...item} />
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* Main Navigation */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Main Navigation
                </h3>
                <div className="space-y-1">
                  {navigationItems.map((item, index) => (
                    <NavigationItem key={index} {...item} />
                  ))}
                </div>
              </div>

              {/* Reports Section */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center space-x-2">
                  <BarChart3 className="h-4 w-4" />
                  <span>Reports & Analytics</span>
                </h3>
                <div className="space-y-1">
                  {reportsItems.map((item, index) => (
                    <NavigationItem key={index} {...item} />
                  ))}
                </div>
              </div>

              {/* Admin Section */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center space-x-2">
                  <Settings className="h-4 w-4" />
                  <span>Administration</span>
                </h3>
                <div className="space-y-1">
                  {adminItems.map((item, index) => (
                    <NavigationItem key={index} {...item} />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* User Profile Section */}
      <div className="p-4 border-t border-gray-200">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold">
            {user?.name ? user.name.substring(0, 2).toUpperCase() : 'AD'}
          </div>
          <div className="flex-1">
            <p className="font-semibold text-gray-900">{user?.name || 'Admin User'}</p>
            <p className="text-sm text-gray-500">{user?.role || 'Super Administrator'}</p>
          </div>
          <button className="text-gray-400 hover:text-red-500 transition-colors">
            <LogOut className="h-4 w-4" />
          </button>
        </div>

      </div>
    </div>
  );
};

// Legacy exports for backward compatibility
export const mainListItems = <MainNavigation />;
export const secondaryListItems = null; // Now integrated into MainNavigation
